import { CommonModule } from '@angular/common';
import { environment } from '@environments/environment';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AppStore, AuthService, Console, ToastService } from '@core/services';
import { filter, map, startWith } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class Sidebar {
  private readonly authService = inject(AuthService);
  private readonly appStore = inject(AppStore);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  // Signal per lo stato di apertura/chiusura del menu
  readonly isOpen = signal(false);

  // Signals reattivi per i dati utente
  readonly user = this.appStore.user;
  readonly isAuthenticated = computed(() => this.appStore.isAuthenticated());
  
  // Computed signal per verificare se l'utente è admin
  readonly isAdmin = computed(() => this.user()?.roles === 'admin');

  readonly adminMenuOpen = signal(this.isAdminUrl(this.router.url));

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.normalizeUrl(this.router.url)),
      startWith(this.normalizeUrl(this.router.url))
    ),
    { initialValue: this.normalizeUrl(this.router.url) }
  );

  readonly isAdminRouteActive = computed(() => this.isAdminUrl(this.currentUrl() ?? ''));

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.isAdminUrl(this.router.url)) {
          this.adminMenuOpen.set(true);
        }
      });
  }

  /**
   * Toggle dello stato di apertura/chiusura del menu
   */
  toggle(): void {
    const newValue = !this.isOpen();
    this.isOpen.set(newValue);
    // Aggiungi/rimuovi classe al body per gestire il blur
    if (newValue) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }

  /**
   * Chiude il menu
   */
  close(): void {
    this.isOpen.set(false);
    document.body.classList.remove('sidebar-open');
  }

  /**
   * Apre il menu
   */
  open(): void {
    this.isOpen.set(true);
    document.body.classList.add('sidebar-open');
  }

  /**
   * Ottiene il nome completo dell'utente
   */
  getFullName(): string {
    const userData = this.user();
    if (!userData) return 'Utente';
    return `${userData.nome} ${userData.cognome}`.trim() || 'Utente';
  }

  /**
   * Gestisce il logout dell'utente
   */
  logout(): void {
    this.close();
    this.authService.logout().subscribe({
      next: () => {
        this.toastService.presentaToastSuccess('Logout effettuato con successo');
        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : Redirect a /login (logout da sidebar)');
        }
        this.router.navigateByUrl('/login');
      },
      error: (error) => {
        Console.error('Sidebar', { message: 'Errore durante il logout', error });
        this.toastService.presentaToastErrore('Errore durante il logout');
      }
    });
  }

  toggleAdminMenu(): void {
    this.adminMenuOpen.update((open) => !open);
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0];
  }

  private isAdminUrl(url: string): boolean {
    return this.normalizeUrl(url).startsWith('/amministrazione');
  }

  /**
   * Naviga a una route e chiude il menu
   */
  navigateTo(route: string): void {
    if (environment.ENABLE_DEBUG_ALERTS) {
      alert('auth-app : Navigazione a ' + route);
    }
    this.router.navigateByUrl(route);
    this.close();
  }
}
